import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { useScroll } from '@contexts/scroll';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import { type CalculatorPediatrics, type Topics } from '@models/Calculator';
import { ChevronLeft, ChevronRightIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { z } from 'zod';

import { useCalculator } from '../../context';
import Content from '../content';

const symptomaticPediatricDose: CalculatorPediatrics = {
  name: 'Dose Sintomáticos Pediatria',
  sections: [
    {
      title: 'Analgésicos Simples',
      topics: [
        {
          title: 'DIPIRONA GOTAS',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'gotas VO de 6/6 horas',
            doseDefault: 12,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 500,
              ml: 1,
              drops: 20,
            }),
            drugOptions: [
              {
                label: '500mg/1mL/20 gotas',
                value: JSON.stringify({
                  mg: 500,
                  ml: 1,
                  drops: 20,
                }),
              },
            ],
            max: 40,
            observations: [
              'Indicação formal apenas para > 3 meses de idade',
              'Fontes: Sociedade Brasileira de Pediatria, Manejo da Febre Aguda, 2021. / Bula do Profissional de Saúde, Sanofi, 2024',
            ],
            suggestions: [
              'Dose: 10 a 12 mg/Kg/dose (Sociedade Brasileira de Pediatria, 2021) ou 15 a 25 mg/Kg/dose (Bula do Profissional de Saúde,2024)',
              'Dose máxima: 40 gotas por dose',
              'Frequência: a cada 6h',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Dipirona',
          },
        },
        {
          title: 'DIPIRONA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 6/6 horas',
            doseDefault: 12,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 50,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '50mg/1mL',
                value: JSON.stringify({
                  mg: 50,
                  ml: 1,
                }),
              },
            ],
            max: 20,
            observations: [
              'Indicação formal apenas para > 3 meses de idade',
              'Fontes: Sociedade Brasileira de Pediatria, Manejo da Febre Aguda, 2021. / Bula do Profissional de Saúde, Sanofi, 2024',
            ],
            suggestions: [
              'Dose: 10 a 12 mg/Kg/dose (Sociedade Brasileira de Pediatria, 2021) ou 15 a 25 mg/Kg/dose (Bula do Profissional de Saúde,2024)',
              'Dose máxima: 20mL por dose',
              'Frequência: a cada 6h',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Dipirona',
            useDrugMl: true,
          },
        },
        {
          title: 'DIPIRONA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'Repetir a cada 6 horas, se necessário',
            conditions: [
              'De acordo com o peso a cada 6h',
              '<= 8Kg: 0,1 a 0,2mL IM',
              '9 - 15Kg: 0,2 a 0,5mL IV ou IM',
              '16 - 23Kg: 0,3 a 0,8mL IV ou IM',
              '24 - 30Kg: 0,4 a 1,0mL IV ou IM',
              '31 - 45Kg: 0,5 a 1,5mL IV ou IM',
              '46 - 53Kg: 0,8 a 1,5mL IV ou IM',
              '>= 54Kg: 2mL IV ou IM',
            ],
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 1000,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '1000mg/2mL',
                value: JSON.stringify({
                  mg: 1000,
                  ml: 2,
                }),
              },
            ],
            templateSeven: true,
            templateSevenCalc2: true,
            title: 'Dipirona',
          },
        },
        {
          title: 'PARACETAMOL GOTAS',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'gotas VO de 6/6 horas',
            doseDefault: 13.3,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 200,
              ml: 1,
              drops: 15,
            }),
            drugOptions: [
              {
                label: '200mg/1mL/15 gotas',
                value: JSON.stringify({
                  mg: 200,
                  ml: 1,
                  drops: 15,
                }),
              },
            ],
            max: 35,
            suggestions: [
              '10 a 15mg/Kg/dose (máx 75mg/Kg/dia ou 4g/dia)',
              'Na prática: Dar 1 gota/Kg (máx 35 gotas) a cada 6h',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Paracetamol',
          },
        },
        {
          title: 'PARACETAMOL SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 6/6 horas',
            doseDefault: 13.3,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 32,
              ml: 1,
              max: 31,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '32mg/1mL',
                value: JSON.stringify({
                  mg: 32,
                  ml: 1,
                  max: 31,
                }),
              },
              {
                label: '100mg/1mL',
                value: JSON.stringify({
                  mg: 100,
                  ml: 1,
                  max: 10,
                }),
              },
            ],
            suggestions: [
              '10 a 15mg/Kg/dose (máx 75mg/Kg/dia ou 4g/dia)',
              '_',
              'xp. 32mg/1mL: Dar 0,31 a 0,46mL/Kg (máx 31mL) a cada 6h',
              'xp. 100mg/1mL: Dar 0,1 a 0,15mL/Kg (máx 10mL) a cada 6h',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Paracetamol',
            useDrugMl: true,
          },
        },
      ],
    },
    {
      title: 'Anti-Eméticos',
      topics: [
        {
          title: 'BROMOPRIDA GOTAS',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'gotas VO de 8/8 horas',
            doseDefault: 0.17,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 4,
              ml: 1,
              drops: 24,
            }),
            drugOptions: [
              {
                label: '4mg/1mL/24 gotas',
                value: JSON.stringify({
                  mg: 4,
                  ml: 1,
                  drops: 24,
                }),
              },
            ],
            max: 60,
            suggestions: [
              '0,17mg/Kg/dose (máx 30mg/Kg/dia)',
              'Na prática: Dar 1 gota/Kg (máx 60 gotas) a cada 8h',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Bromoprida',
          },
        },
        {
          title: 'BROMOPRIDA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM a cada 8 horas, se necessário',
            applicationUnitSubtitle:
              'Diluir o volume acima em 100mL SF 0,9% se administrado IV',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.33,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '10mg/2mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 2,
                }),
              },
            ],
            multiplyDoseMax: 10,
            observations: ['Para maiores de 1 ano'],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              '0,16 a 0,33mg/Kg/dose (0,5 a 1mg/Kg/dia)',
              'Na prática: Aplicar 0,03 a 0,07mL/Kg (máx 2mL) IV ou IM a cada 8h',
              '_',
              'Para administração IV, diluir o volume calculado em SF 0,9% ou SG 5% e infundir lento',
            ],
            templateOne: true,
            title: 'Bromoprida',
          },
        },
        {
          title: 'ONDANSETRONA COMP.',
          content: {
            applicationUnitMeasure: 'a cada 8-12 horas, se necessário',
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 4,
            }),
            drugFieldWidth: 0.3,
            drugOptions: [
              {
                label: '4mg',
                value: JSON.stringify({
                  mg: 4,
                }),
              },
              {
                label: '8mg',
                value: JSON.stringify({
                  mg: 8,
                }),
              },
            ],
            observations: ['IV/IM: Acima de 1-6 meses', 'VO: Acima de 2 anos'],
            suggestions: [
              '< 12 anos: 0,15mg/Kg/dose a cada 8-12h (máx 8mg/dia)',
              '> 12 anos: 4mg a cada 8h ou 8mg a cada 12h',
            ],
            templateSeven: true,
            templateSevenCalc3: true,
            title: 'Ondansetrona',
          },
        },
        {
          title: 'ONDANSETRONA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM a cada 8 horas, se necessário',
            applicationUnitSubtitle: `Para administração IV, diluir o volume em 25-50mL de SF 0,9% ou SG 5% e infundir em pelo menos 15 min`,
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.15,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 4,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '4mg/2mL',
                value: JSON.stringify({
                  mg: 4,
                  ml: 2,
                }),
              },
              {
                label: '8mg/4mL',
                value: JSON.stringify({
                  mg: 8,
                  ml: 4,
                }),
              },
            ],
            multiplyDoseMax: 8,
            observations: [
              'IV / IM: Acima de 1-6 meses',
              'VO: Acima de 2 anos',
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              '0,15mg/Kg/dose (máx 3x)',
              'Na prática: Aplicar 0,08mL/Kg, IV ou IM a cada 8h (máx 2mL a cada 8h ou 4mL a cada 12h)',
              '_',
              'Para administração IV, diluir o volume em 25-50mL de SF 0,9% ou SG 5% e infundir em pelo menos 15 min',
            ],
            templateOne: true,
            title: 'Ondansetrona',
          },
        },
        {
          title: 'DIMENIDRINATO SOL. ORAL',
          content: {
            applicationUnitMeasure: 'de 8/8 horas',
            drugDefault: JSON.stringify({
              mg: 2.5,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '2,5mg/1mL',
                value: JSON.stringify({
                  mg: 2.5,
                  ml: 1,
                }),
              },
            ],
            observations: ['Para maiores de 2 anos'],
            suggestions: [
              '1,25mg/Kg (0,5mL/Kg)',
              '_',
              'Na prática:',
              '> 12 anos: 20-40mL a cada 4-6h (máx 160mL/dia)',
              '6-12 anos: 10-20mL a cada 6-8h (máx 60mL/dia)',
              '2-6 anos: 5-10mL a cada 6-8h (máx 30mL/dia)',
            ],
            templateSeven: true,
            templateSevenCalc4: true,
            title: 'Dimenidrinato',
          },
        },
      ],
    },
    {
      title: 'AINEs',
      topics: [
        {
          title: 'IBUPROFENO GOTAS',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'gotas VO de 8/8 horas',
            doseDefault: 10,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 100,
              ml: 1,
              drops: 10,
              max: 20,
            }),
            drugOptions: [
              {
                label: '100mg/1mL/10 gotas',
                value: JSON.stringify({
                  mg: 100,
                  ml: 1,
                  drops: 10,
                  max: 20,
                }),
              },
              {
                label: '50mg/1mL/10 gotas',
                value: JSON.stringify({
                  mg: 50,
                  ml: 1,
                  drops: 10,
                  max: 40,
                }),
              },
            ],
            observations: [
              'Para maiores de 6 meses',
              'Utilizar AINEs pelo menor tempo e na menor dose efetiva possível',
            ],
            suggestions: [
              '5 a 10mg/Kg/dose (máx 800mg/dia)',
              '_',
              '100mg/1mL/10 gotas: 1 gota/Kg (máx 20 gotas/dose) a cada 8-6h (máx 80 gotas/dia)',
              '_',
              '50mg/1mL/10 gotas: 1 a 2 gotas/Kg (máx 40 gotas/dose) a cada 8-6h (máx 160 gotas/dia)',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Ibuprofeno',
          },
        },
        {
          title: 'IBUPROFENO SOL. ORAL',
          content: {
            applicationUnitMeasure: 'mL VO de 8/8 horas',
            doseDefault: 10,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 30,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '30mg/1mL',
                value: JSON.stringify({
                  mg: 30,
                  ml: 1,
                }),
              },
            ],
            max: 7,
            observations: [
              'Para maiores de 6 meses',
              'Utilizar AINEs pelo menor tempo a menor dose efetiva possível',
            ],
            suggestions: [
              '5 a 10mg/Kg/dose (máx 840mg/dia)',
              '_',
              'Na prática: 0,16 a 0,33mL/Kg (máx 7mL) a cada 8-6h',
              'Máx de 7mL/dose (210mg) e 28mL/dia (840mg)',
            ],
            templateSix: true,
            templateSixCalc2: true,
            title: 'Ibuprofeno',
          },
        },
        {
          title: 'CETOROLACO SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM a cada 8h, se necessário',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.5,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 30,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '30mg/1mL',
                value: JSON.stringify({
                  mg: 30,
                  ml: 1,
                }),
              },
            ],
            multiplyDoseMax: 30,
            observations: [
              'Para maiores de 2 anos',
              'Utilizar AINEs pelo menor tempo e na menor dose efetiva possível',
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              '0,5 a 1mg/Kg/dose (máx 2mg/Kg/dia ou 60 mg/dia)',
              '_',
              'Na prática:',
              'Intramuscular: Aplicar 0,03mL/Kg (máx 1mL) IM',
              'Intravenoso: Aplicar 0,017 a 0,033mL/Kg (máx 1mL) IV bolus (> 15 seg)',
            ],
            templateOne: true,
            title: 'Cetorolaco',
          },
        },

        {
          title: 'CETOPROFENO GOTAS',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'gotas VO a cada 6-8 horas',
            doseDefault: 1,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 20,
              ml: 1,
              drops: 20,
              max: 50,
            }),
            drugOptions: [
              {
                label: '20mg/1mL/20 gotas',
                value: JSON.stringify({
                  mg: 20,
                  ml: 1,
                  drops: 20,
                  max: 50,
                }),
              },
            ],
            observations: [
              'Para maiores de 1 ano',
              'Utilizar AINEs pelo menor tempo e na menor dose efetiva possível',
            ],
            suggestions: ['1 mg/Kg/dose (máx 50mg/dose e 300mg/dia)'],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Cetoprofeno',
          },
        },
      ],
    },
    {
      title: 'Corticoides',
      topics: [
        {
          title: 'PREDNISONA COMP.',
          content: {
            applicationUnitMeasure: 'comp VO por dia',
            doseDefault: 0.5,
            doseUnitMeasure: 'mg/Kg/dia',
            drugDefault: JSON.stringify({
              mg: 5,
            }),
            drugFieldWidth: 0.3,
            drugOptions: [
              {
                label: '5mg',
                value: JSON.stringify({
                  mg: 5,
                }),
              },
              {
                label: '20mg',
                value: JSON.stringify({
                  mg: 20,
                }),
              },
            ],
            observations: [
              'Preferir doses únicas pela manhã para reduzir o risco de insônia induzida  por corticoides',
            ],
            suggestions: ['0,5 a 2mg/Kg/dia'],
            templateSix: true,
            title: 'Prednisona',
          },
        },
        {
          title: 'PREDNISOLONA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 24/24 horas',
            doseDefault: 0.5,
            doseUnitMeasure: 'mg/Kg/dia',
            drugDefault: JSON.stringify({
              mg: 3,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
              {
                label: '3mg/1mL',
                value: JSON.stringify({
                  mg: 3,
                  ml: 1,
                }),
              },
            ],
            observations: [
              'Preferir doses únicas pela manhã para reduzir o risco de insônia induzida  por corticoides',
            ],
            suggestions: ['0,5 a 2mg/Kg/dia'],
            templateSix: true,
            title: 'Prednisolona',
            useDrugMl: true,
          },
        },
        {
          title: 'DEXAMETASONA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 24/24 horas',
            doseDefault: 0.07,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dia',
            drugDefault: JSON.stringify({
              mg: 0.5,
              ml: 5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '0,5mg/5mL',
                value: JSON.stringify({
                  mg: 0.5,
                  ml: 5,
                }),
              },
            ],
            observations: [
              'Preferir doses únicas pela manhã para reduzir o risco de insônia induzida  por corticoides',
            ],
            suggestions: ['0,07 a 0,3mg/Kg/dia'],
            templateSix: true,
            title: 'Dexametasona',
            useDrugMl: true,
          },
        },
        {
          title: 'METILPREDNISOLONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 0.5,
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '40mg',
                value: JSON.stringify({
                  mg: 40,
                }),
              },
              {
                label: '125mg',
                value: JSON.stringify({
                  mg: 125,
                }),
              },
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 0,8mg/Kg de metilprednisolona',
            ],
            salineDefault: 25,
            salineLabel: 'SF 0,9%',
            suggestions: ['500mg + 25mL SF 0,9%', '0,5 a 1mg/Kg/dose'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Metilprednisolona',
          },
        },
        {
          title: 'HIDROCORTISONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 2.5,
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.3,
            drugOptions: [
              {
                label: '100mg',
                value: JSON.stringify({
                  mg: 100,
                }),
              },
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 4mg/Kg de hidrocortisona',
            ],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: ['500mg + 5mL SF 0,9%', '2,5 a 5mg/Kg/dose'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Hidrocortisona',
          },
        },
        {
          title: 'DEXAMETASONA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.09,
            doseFixed: 2,
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 2.5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '10mg/2,5mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 2.5,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 0,1mg/Kg de dexametasona',
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: ['Não diluir', '0,09 a 0,19mg/Kg/dose'],
            templateOne: true,
            title: 'Dexametasona',
          },
        },
      ],
    },
    {
      title: 'Anti-Alérgicos e Cort. Nasais',
      topics: [
        {
          title: 'LORATADINA SOL. ORAL',
          content: {
            drugDefault: JSON.stringify({
              mg: 1,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
            ],
            observations: ['Para maiores de 2 anos'],
            suggestions: [
              '>= 30Kg: 10mL uma vez ao dia',
              '< 30Kg: 5mL uma vez ao dia',
            ],
            templateSeven: true,
            templateSevenCalc6: true,
            title: 'Loratadina',
          },
        },
        {
          title: 'DESLORATADINA SOL. ORAL',
          content: {
            drugDefault: JSON.stringify({
              mg: 0.5,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugNameBig: true,
            drugOptions: [
              {
                label: '0,5mg/1mL',
                value: JSON.stringify({
                  mg: 0.5,
                  ml: 1,
                }),
              },
            ],
            observations: ['Para maiores de 6 meses'],
            suggestions: [
              '>= 12 anos: 10mL 1x/dia',
              '6-11 anos: 5mL 1x/dia',
              '1-5 anos: 2,5mL 1x/dia',
              '6-11 meses: 2mL 1x/dia',
            ],
            templateSeven: true,
            templateSevenCalc7: true,
            title: 'Desloratadina',
          },
        },
        {
          title: 'BUDESONIDA SPRAY',
          content: {
            drugDefault: JSON.stringify({
              mg: 32,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '32mcg/jato',
                value: JSON.stringify({
                  mg: 32,
                }),
              },
              {
                label: '64mcg/jato',
                value: JSON.stringify({
                  mg: 64,
                }),
              },
            ],
            observations: ['Para maiores de 4 anos'],
            suggestions: [
              'Utilizar até 256mcg/dia (2 aplicações de 64mcg em cada narina ou 4 aplicações de 32mcg em cada narina)',
              'A dose diária de 356mcg pode ser administrada apenas uma vez ao dia ou dividida em duas administrações de 126mcg/dia pela manhã e à noite',
              '_',
              'Após melhora dos sintomas, reduzir para menor dose efetiva',
            ],
            templateSeven: true,
            templateSevenCalc8: true,
            title: 'Budesonida',
          },
        },
        {
          title: 'FUROATO DE FLUTICASONA SPRAY',
          content: {
            drugDefault: JSON.stringify({
              mg: 27.5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '27,5mcg/jato',
                value: JSON.stringify({
                  mg: 27.5,
                }),
              },
            ],
            observations: ['Para maiores de 2 anos'],
            suggestions: [
              '1 a 2 jatos (27,5mcg por jato) em cada narina uma vez ao dia',
              '_',
              'Iniciar com 55mcg/dia (1 jato/narina 1x/dia), podendo usar 110mcg/dia (2 jatos/narina 1x/dia)',
              '_',
              'Quando o controle dos sintomas é atingido, recomenda-se a redução da dose para 1 jato/narina 1x/dia (dose diária: 55mcg)',
            ],
            templateSeven: true,
            templateSevenCalc9: true,
            title: 'Furoato de Fluticasona',
          },
        },
      ],
    },
    {
      title: 'Laxativos',
      topics: [
        {
          title: 'LACTULOSE SOL. ORAL',
          content: {
            drugDefault: JSON.stringify({
              mg: 667,
              ml: 1,
            }),
            drugFieldWidth: 0.58,
            drugOptions: [
              {
                label: '667mg/1mL (fr 120mL)',
                value: JSON.stringify({
                  mg: 667,
                  ml: 1,
                }),
              },
            ],
            observations: [
              'Ajustar dose para obter 2 a 3 evacuações por dia',
              'Pode ser administrada preferencialmente em uma única tomada pela manhã ou à noite, sozinha ou com alimentos, ou ainda misturada a suco de fruta, leite, iogurte, água ou qualquer líquido',
            ],
            suggestions: [
              '> 12 anos: Tomar 15 a 30mL por dia',
              '6-12 anos: Dar 10 a 15mL por dia',
              '1-5 anos: Dar 5 a 10mL por dia',
              '< 1 ano: Dar 5mL por dia',
            ],
            templateSeven: true,
            templateSevenCalc10: true,
            title: 'Lactulose',
          },
        },
        {
          title: 'PEG 4000 SACHÊ',
          content: {
            applicationUnitMeasure: 'mL VO uma vez ao dia',
            applicationUnitSubtitle:
              'Ajustar dose conforme número de evacuações',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'sachê',
                value: '1',
              },
            ],
            doseDefault: 0.5,
            doseUnitMeasure: 'g/kg/dia',
            drugDefault: JSON.stringify({
              mg: 5,
            }),
            drugFieldWidth: 0.25,
            drugOptions: [
              {
                label: '5g',
                value: JSON.stringify({
                  mg: 5,
                }),
              },
              {
                label: '10g',
                value: JSON.stringify({
                  mg: 10,
                }),
              },
            ],
            multiplyDoseMax: 10,
            observations: [
              'PEG = Polietilenoglicol',
              '_',
              'As formulações sem eletrólitos podem ser prescritas e manipuladas de acordo com o peso da criança e a dose, em envelopes individuais (pó)',
              'A adição de eletrólitos ao PEG torna o fármaco de sabor salgado, contribuindo para a diminuição de adesão ao tratamento',
            ],
            salineDefault: 10,
            salineLabel: 'Líquido',
            suggestions: [
              '0,5g/Kg/dia',
              '_',
              'O sachê pode ser dissolvido em líquidos como água, sucos ou leite, e oferecido geralmente uma vez ao dia',
            ],
            templateOne: true,
            templateOneCalc3: true,
            title: 'PEG 4000',
          },
        },
        {
          title: 'GLICERINA SUPOSITÓRIO',
          content: {
            drugDefault: JSON.stringify({
              mg: 1.44,
            }),
            drugNameBig: true,
            drugOptions: [
              {
                label: '1,44g de glicerol/sup',
                value: JSON.stringify({
                  mg: 1.44,
                }),
              },
            ],
            suggestions: [
              '1 supositório ao dia quando necessário ou a critério médico',
              '_',
              'Introduzir o supositório no reto e procurar retê-lo até que advenha a vontade de evacuar',
              '_',
              'De um modo geral, o efeito desejado de evacuação das fezes retidas com a constipação intestinal é alcançado alguns minutos após a introdução do supositório no reto',
              '_',
              'Pode-se deixar o supositório atuar por 15 a 30 minutos',
              'Não é necessário que o produto se dissolva completamente para que produza o efeito desejado',
            ],
            templateSeven: true,
            templateSevenCalc11: true,
            title: 'Glicerina supositório',
          },
        },
      ],
    },
    {
      title: 'Opioides',
      topics: [
        {
          title: 'MORFINA GOTAS',
          content: {
            applicationUnitMeasure: 'gotas VO a cada 4-12h',
            doseDefault: 0.2,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 1,
              drops: 32,
            }),
            drugOptions: [
              {
                label: '10mg/1mL/32 gotas',
                value: JSON.stringify({
                  mg: 10,
                  ml: 1,
                  drops: 32,
                }),
              },
            ],
            max: 64,
            observations: [
              'Para maiores de 2 anos',
              'O produto pode ser diluído em suco de fruta antes da ingestão para melhorar o sabor',
            ],
            suggestions: [
              '0,1 a 0,5mg/Kg/dose',
              'A dose máxima diária deve ser individualizada de acordo com a intensidade da dor, levando-se em consideração a idade, o peso e a tolerabilidade do paciente',
              'Máximo por dose: 20mg (64 gotas)',
              'Máximo diário: 0,5mg/Kg a cada 3-4h',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Morfina',
          },
        },
        {
          title: 'MORFINA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV, IM ou SC (off-label)',
            applicationUnitSubtitle:
              'Repetir dose a cada 4-12h conforme necessidade.',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.04,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 2,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '2mg/2mL',
                value: JSON.stringify({
                  mg: 2,
                  ml: 2,
                }),
              },
              {
                label: '0,1mg/1mL',
                value: JSON.stringify({
                  mg: 0.1,
                  ml: 1,
                }),
              },
              {
                label: '0,2mg/1mL',
                value: JSON.stringify({
                  mg: 0.2,
                  ml: 1,
                }),
              },
              {
                label: '10mg/1mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 1,
                }),
              },
            ],
            multiplyDoseMax: 10,
            observations: [
              'Não diluir amp 0,2mg/mL e 0,1mg/mL',
              'A amp de 10mg/mL pode ser diluída em 9mL AD',
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              '0,025 a 0,05mg/Kg/dose a cada 2-4h (máx 10-20mg/dose e 60-80mg/dia)',
              '_',
              'A dose deve ser ajustada de acordo com a resposta individual do paciente até que seja obtido um nível aceitável de analgesia, levando em consideração a melhoria da intensidade da dor e a tolerabilidade da morfina pelo paciente, sem a ocorrência de efeitos adversos intoleráveis',
            ],
            templateOne: true,
            title: 'Morfina',
          },
        },
        {
          title: 'TRAMADOL GOTAS',
          content: {
            applicationUnitMeasure: 'gotas VO a cada 4-12h',
            doseDefault: 1,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 100,
              ml: 1,
              drops: 40,
            }),
            drugOptions: [
              {
                label: '100mg/1mL/40 gotas',
                value: JSON.stringify({
                  mg: 100,
                  ml: 1,
                  drops: 40,
                }),
              },
            ],
            max: 40,
            observations: ['Para maiores de 1 ano'],
            suggestions: [
              '1 a 2mg/Kg/dose',
              'Máximo: 8mg/Kg/dia ou 400mg/dia (oque for menor)',
              '_',
              'Cada gota = 2,5mg / 40 gotas = 100mg',
            ],
            templateSix: true,
            templateSixCalcDrops: true,
            title: 'Tramadol',
          },
        },
        {
          title: 'TRAMADOL SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV, IM ou SC',
            applicationUnitSubtitle:
              'Repetir dose a cada 4-12h conforme necessidade.',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 1,
            doseUnitMeasure: 'mg/Kg/dose',
            drugDefault: JSON.stringify({
              mg: 50,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '50mg/1mL',
                value: JSON.stringify({
                  mg: 50,
                  ml: 1,
                }),
              },
              {
                label: '100mg/2mL',
                value: JSON.stringify({
                  mg: 100,
                  ml: 2,
                }),
              },
            ],
            multiplyDoseMax: 50,
            observations: ['Para maiores de 1 ano'],
            salineDefault: 9,
            salineLabel: 'SF 0,9%',
            suggestions: [
              '1 a 2mg/Kg/dose',
              'Máximo: 8mg/Kg/dia ou 400mg/dia (o que for menor)',
              '_',
              'A solução para injeção deve ser diluída na solução de infusão e infundida lentamente',
              'Amp 50mg/1mL: Diluir em 9mL AD',
              'Amp 100mg/2mL: Diluir 18mL AD',
            ],
            templateOne: true,
            title: 'Tramadol',
          },
        },
      ],
    },
  ],
};

const formSchema = z.object({
  weight: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ageMonth: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ageYear: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function PediatricSymptomaticDose() {
  const { onScrollTo } = useScroll();
  const [topics, setTopics] = useState<Topics[]>([]);
  const { ageMonth, ageYear, setAgeMonth, setAgeYear, setWeight, weight } =
    useCalculator();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      weight: weight.toFixed(1),
      ageMonth: String(ageMonth),
      ageYear: String(ageYear),
    },
  });

  const handleSetTopics = (topics: Topics[]) => {
    onScrollTo();
    setTopics(topics);
  };

  const handleClearTopics = () => {
    setTopics([]);
    onScrollTo();
  };

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  const handleChangeAgeYear = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAgeYear(valueParsed);
    },
    [setAgeYear],
  );

  const handleChangeAgeMonth = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAgeMonth(valueParsed);
    },
    [setAgeMonth],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
      if (name === 'ageYear' && value.ageYear) {
        handleChangeAgeYear(value.ageYear);
      }
      if (name === 'ageMonth' && value.ageMonth) {
        handleChangeAgeMonth(value.ageMonth);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setWeight(0);
    form.setValue('weight', '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <PageTitle hasBackButton title="Dose sintomáticos pediatria" />
      <Form {...form}>
        <form
          className="bg-background sticky top-0 z-[9] py-4"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-center gap-2">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem className="flex flex-col items-center">
                  <FormLabel>Peso</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                  <FormDescription>Kg</FormDescription>
                </FormItem>
              )}
            />
            <div className="flex flex-col items-center justify-end gap-2">
              <Label>Idade</Label>
              <div className="flex flex-row gap-2">
                <FormField
                  control={form.control}
                  name="ageYear"
                  render={({ field: { ref, ...rest } }) => (
                    <FormItem className="flex flex-col items-center">
                      <FormControl>
                        <NumericFormat
                          className="[&_input]:w-20 [&_input]:text-center"
                          customInput={Input}
                          getInputRef={ref}
                          maxLength={2}
                          {...rest}
                        />
                      </FormControl>
                      <FormDescription>Anos</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ageMonth"
                  render={({ field: { ref, ...rest } }) => (
                    <FormItem className="flex flex-col items-center">
                      <FormControl>
                        <NumericFormat
                          className="[&_input]:w-20 [&_input]:text-center"
                          customInput={Input}
                          getInputRef={ref}
                          maxLength={2}
                          {...rest}
                        />
                      </FormControl>
                      <FormDescription>Meses</FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
          {!!topics.length && (
            <div className="mx-auto max-w-3xl">
              <button
                className="text-foreground mt-4 flex cursor-pointer flex-row items-center text-lg font-medium"
                onClick={handleClearTopics}
                title="Voltar"
                type="button">
                <ChevronLeft className="size-7" strokeWidth={2} />
                <h1>Voltar</h1>
              </button>
            </div>
          )}
        </form>
      </Form>
      <div
        className={cn(
          'mx-auto max-w-3xl transition-all duration-500',
          topics.length ? 'animate-out fade-out hidden' : 'animate-in fade-in',
        )}>
        {symptomaticPediatricDose.sections.map((item) => (
          <button
            className="ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 flex w-full flex-1 cursor-pointer items-start justify-between gap-4 rounded-md py-4 text-left text-lg font-medium uppercase transition-all hover:underline hover:opacity-70 focus-visible:outline-1 focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50"
            key={item.title}
            onClick={() => handleSetTopics(item.topics)}>
            {item.title}
            <ChevronRightIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
          </button>
        ))}
      </div>
      <div
        className={cn(
          'mx-auto max-w-3xl transition-all duration-500',
          topics.length ? 'animate-in fade-in' : 'animate-out fade-out hidden',
        )}>
        {topics.map((item) => (
          <Content
            applicationText={item.content.applicationText}
            applicationUnitMeasure={item.content.applicationUnitMeasure}
            applicationUnitMeasure2={item.content.applicationUnitMeasure2}
            applicationUnitSubtitle={item.content.applicationUnitSubtitle}
            chargeDefault={item.content.chargeDefault}
            chargeTwoDefault={item.content.chargeTwoDefault}
            conditions={item.content.conditions}
            description={item.content.description}
            diluteDefault={item.content.diluteDefault}
            diluteText={item.content.diluteText}
            diluteUnitMeasureDefault={item.content.diluteUnitMeasureDefault}
            diluteUnitMeasureOptions={item.content.diluteUnitMeasureOptions}
            doseDefault={item.content.doseDefault}
            doseFixed={item.content.doseFixed}
            doseTwoCalc2={item.content.doseTwoCalc2}
            doseTwoDefault={item.content.doseTwoDefault}
            doseUnitMeasure={item.content.doseUnitMeasure}
            drugDefault={item.content.drugDefault}
            drugFieldWidth={item.content.drugFieldWidth}
            drugNameBig={item.content.drugNameBig}
            drugOptions={item.content.drugOptions}
            hideChargeThree={item.content.hideChargeThree}
            hideDoseTwoConditionText={item.content.hideDoseTwoConditionText}
            key={item.title}
            max={item.content.max}
            multiplyDoseMax={item.content.multiplyDoseMax}
            multiplyDoseTwoMax={item.content.multiplyDoseTwoMax}
            nebMax={item.content.nebMax}
            observations={item.content.observations}
            salineDefault={item.content.salineDefault}
            salineLabel={item.content.salineLabel}
            showBIC={item.content.showBIC}
            showDoseTwo={item.content.showDoseTwo}
            showSpeed={item.content.showSpeed}
            speedCalc2={item.content.speedCalc2}
            speedText={item.content.speedText}
            speedUnitMeasure={item.content.speedUnitMeasure}
            suggestions={item.content.suggestions}
            templateEight={item.content.templateEight}
            templateFive={item.content.templateFive}
            templateFour={item.content.templateFour}
            templateFourCalc2={item.content.templateFourCalc2}
            templateOne={item.content.templateOne}
            templateOneCalc2={item.content.templateOneCalc2}
            templateOneCalc3={item.content.templateOneCalc3}
            templateSeven={item.content.templateSeven}
            templateSevenCalc10={item.content.templateSevenCalc10}
            templateSevenCalc11={item.content.templateSevenCalc11}
            templateSevenCalc12={item.content.templateSevenCalc12}
            templateSevenCalc2={item.content.templateSevenCalc2}
            templateSevenCalc3={item.content.templateSevenCalc3}
            templateSevenCalc4={item.content.templateSevenCalc4}
            templateSevenCalc5={item.content.templateSevenCalc5}
            templateSevenCalc6={item.content.templateSevenCalc6}
            templateSevenCalc7={item.content.templateSevenCalc7}
            templateSevenCalc8={item.content.templateSevenCalc8}
            templateSevenCalc9={item.content.templateSevenCalc9}
            templateSix={item.content.templateSix}
            templateSixCalc2={item.content.templateSixCalc2}
            templateSixCalcDrops={item.content.templateSixCalcDrops}
            templateThree={item.content.templateThree}
            templateTwo={item.content.templateTwo}
            title={item.title}
            useDrugMl={item.content.useDrugMl}
          />
        ))}
      </div>
    </div>
  );
}

export default PediatricSymptomaticDose;
